import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarOff, Plus, Trash2, Snowflake } from "lucide-react";
import { toast } from "sonner";

function formatDateIT(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function BusinessClosures() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/closures");
      setList(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/closures");
        if (active) setList(data || []);
      } catch (e) {
        if (active) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!start || !end) { toast.error("Inserisci entrambe le date"); return; }
    if (start > end) { toast.error("La data fine non può precedere la data inizio"); return; }
    setBusy(true);
    try {
      await api.post("/admin/closures", {
        start_date: start,
        end_date: end,
        reason: reason || "Chiusura aziendale",
      });
      toast.success("Chiusura aggiunta");
      setStart(""); setEnd(""); setReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la chiusura ${formatDateIT(c.start_date)} → ${formatDateIT(c.end_date)}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/closures/${c.id}`);
      toast.success("Chiusura eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="business-closures-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">Calendario aziendale</p>
        <h1 className="font-display text-4xl font-bold mt-1 flex items-center gap-3">
          <CalendarOff className="w-8 h-8 text-[#FFD700]" /> Chiusure
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          Blocca interi periodi del calendario (chiusura invernale, ferie, manutenzione). Durante questi giorni nessun cliente potrà prenotare online.
        </p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-4" data-testid="closure-form">
        <div className="flex items-center gap-2 text-sm text-zinc-300">
          <Snowflake className="w-4 h-4 text-sky-400" /> Nuova chiusura
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-zinc-400">Data inizio</Label>
            <Input type="date" min={today} value={start} onChange={(e) => setStart(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="closure-start-input" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Data fine (inclusa)</Label>
            <Input type="date" min={start || today} value={end} onChange={(e) => setEnd(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="closure-end-input" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Motivo (opzionale)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Chiusura invernale" className="bg-zinc-950 border-zinc-800 mt-1" maxLength={200} data-testid="closure-reason-input" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy || !start || !end} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="closure-add-button">
            <Plus className="w-4 h-4 mr-1" /> {busy ? "Salvo…" : "Aggiungi chiusura"}
          </Button>
        </div>
      </form>

      <div className="border border-zinc-800 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900/40">
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Dal</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Al</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Motivo</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Creato il</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-zinc-500 text-center py-8">Caricamento…</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-zinc-500 text-center py-12">
                <CalendarOff className="w-8 h-8 mx-auto mb-2 opacity-50" /> Nessuna chiusura programmata
              </TableCell></TableRow>
            ) : (
              list.map((c) => {
                const past = c.end_date < today;
                return (
                  <TableRow key={c.id} className={`border-zinc-800 hover:bg-zinc-900/20 ${past ? "opacity-50" : ""}`} data-testid={`closure-row-${c.id}`}>
                    <TableCell className="text-zinc-100 font-mono text-sm">{formatDateIT(c.start_date)}</TableCell>
                    <TableCell className="text-zinc-100 font-mono text-sm">{formatDateIT(c.end_date)}</TableCell>
                    <TableCell className="text-zinc-300 text-sm">{c.reason || "—"}</TableCell>
                    <TableCell className="text-zinc-500 text-xs">{(c.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => remove(c)} disabled={busy}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                              data-testid={`closure-delete-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
